// Shared types and helpers for the AI advice card component.

// Matches the BadgeColor type in badge.tsx (not exported there, duplicated here)
export type BadgeColor = "accent" | "success" | "danger" | "warn" | "info" | "muted";

// ---- Advice shapes returned by /wind/:id/ai and /grid/:id/ai ---------------

export type Regime = "T" | "R" | "V" | "Q";

export interface WindPreCycleAdvice {
  open: boolean;
  dir: "long" | "short";
  conf: number;
  regime: Regime;
  tp: number | null;
  sl: number | null;
  why: string;
  risks: string[];
}

export interface WindMonitorAdvice {
  cut: boolean;
  cut_why: string | null;
  tp: number | null;
  sl: number | null;
  regime: Regime;
  why: string;
  risks: string[];
}

export interface GridAdvice {
  act: "HOLD" | "PAUSE" | "RESUME" | "CUT_LIMIT" | "CUT_MARKET";
  conf: number;
  lim_px: number | null;
  regime: Regime;
  why: string;
  risks: string[];
  params: object | null;
}

export type AiAdvice = WindPreCycleAdvice | WindMonitorAdvice | GridAdvice;

// ---- Display helpers -------------------------------------------------------

export const REGIME_LABEL: Record<Regime, string> = {
  T: "Trending",
  R: "Ranging",
  V: "Volatile",
  Q: "Quiet",
};

export const REGIME_COLOR: Record<Regime, BadgeColor> = {
  T: "success",
  R: "info",
  V: "warn",
  Q: "muted",
};

export function confColor(conf: number): string {
  if (conf >= 0.7) return "#22c55e"; // success
  if (conf >= 0.4) return "#eab308"; // warn
  return "#ef4444";                  // danger
}

export function getMainAction(advice: AiAdvice): string {
  if ("act" in advice) return advice.act;
  if ("open" in advice) return advice.open ? `OPEN ${advice.dir.toUpperCase()}` : "SKIP";
  if ("cut" in advice) return advice.cut ? `CUT — ${advice.cut_why ?? ""}` : "HOLD";
  return "—";
}

export function getConf(advice: AiAdvice): number | null {
  if ("conf" in advice) return advice.conf;
  return null;
}

export function getRegime(advice: AiAdvice): Regime | null {
  if ("regime" in advice) return advice.regime as Regime;
  return null;
}

export function getTpSl(advice: AiAdvice): { tp: number | null; sl: number | null } {
  if ("tp" in advice && "sl" in advice) return { tp: advice.tp, sl: advice.sl };
  return { tp: null, sl: null };
}

export function parseAdvice(json: string | null | undefined): AiAdvice | null {
  if (!json) return null;
  try { return JSON.parse(json) as AiAdvice; } catch { return null; }
}
